import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import logo from "../images/logos/logo.webp";

function Navbar() {
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const location = useLocation();
	const menuRef = useRef(null);

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 768) setMenuOpen(false);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const navItems = [
		{ path: "/about", label: "About" },
		{ path: "/portfolio", label: "Portfolio" },
		{ path: "/contact", label: "Contact" }
	];

	return (
		<motion.nav
			ref={menuRef}
			initial={{ y: -100 }}
			animate={{ y: 0 }}
			className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
				scrolled || menuOpen
					? "bg-black/50 backdrop-blur-lg shadow-lg shadow-black/10"
					: "bg-black/20 backdrop-blur-sm"
			} border-b border-white/10`}
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center py-2">
					{/* Logo */}
					<Link to="/" onClick={() => setMenuOpen(false)}>
						<motion.div
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							className="cursor-pointer flex items-center gap-3"
						>
							<img
								src={logo}
								alt="MR Logo"
								className="w-16 h-16 rounded-full object-contain"
							/>
							<span className="hidden sm:block text-xl font-bold text-white">
								Matteo Robidoux
							</span>
						</motion.div>
					</Link>

					{/* Desktop Navigation */}
					<div className="hidden md:flex items-center gap-1 bg-white/5 rounded-lg p-1 border border-white/10">
						{navItems.map((item) => (
							<Link key={item.path} to={item.path}>
								<motion.div
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									className={`relative px-5 py-2 rounded-md font-medium transition-colors ${
										location.pathname === item.path
											? "text-white"
											: "text-gray-400 hover:text-gray-200"
									}`}
								>
									{location.pathname === item.path && (
										<motion.div
											layoutId="navbar-pill"
											className="absolute inset-0 bg-white/15 rounded-md"
											transition={{
												type: "spring",
												bounce: 0.2,
												duration: 0.6
											}}
										/>
									)}
									<span className="relative z-10">{item.label}</span>
								</motion.div>
							</Link>
						))}
					</div>

					{/* Mobile menu button */}
					<button
						onClick={() => setMenuOpen(!menuOpen)}
						className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white transition-colors"
					>
						<svg
							className="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							{menuOpen ? (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							) : (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							)}
						</svg>
					</button>
				</div>
			</div>

			{/* Mobile Navigation */}
			<AnimatePresence>
				{menuOpen && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						className="md:hidden bg-black/60 backdrop-blur-lg border-t border-white/10"
					>
						<div className="px-4 py-4 space-y-1">
							{navItems.map((item, index) => (
								<Link
									key={item.path}
									to={item.path}
									onClick={() => setMenuOpen(false)}
								>
									<motion.div
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: index * 0.1 }}
										className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
											location.pathname === item.path
												? "bg-white/15 text-white"
												: "text-gray-300 hover:text-white hover:bg-white/10"
										}`}
									>
										{item.label}
									</motion.div>
								</Link>
							))}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</motion.nav>
	);
}

export default Navbar;
